import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Skeleton from '@material-ui/lab/Skeleton';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import Fade from '@mui/material/Fade';
import { FDPagination, FDStack } from '@facet-wealth/design';
import { noop } from 'Helpers/PropTypes';
import TemplateListItem from './TemplateListItem';

const styles = theme => ({
  root: {
    width: 450,
    borderBottom: `1px solid ${theme.palette.facet.lightGray}`,
    paddingBottom: theme.spacing(1),
    margin: theme.spacing(1, 0),
    '&:nth-child(even)': {
      marginTop: theme.spacing(2),
    },
  },
  templateListTitle: {
    padding: theme.spacing(0, 2),
    textTransform: 'uppercase',
    fontSize: theme.typography.body1.fontSize,
  },
  templateList: {
    maxHeight: 300,
    overflowY: 'scroll',
  },
  templateListNoItems: {
    padding: theme.spacing(0, 2),
    marginBottom: theme.spacing(1),
    fontSize: theme.typography.body1.fontSize,
  },
  savedTemplateItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  thumtackIcon: {
    fontSize: '0.8em',
    color: theme.palette.grey[300],
    transition: 'background 0.3s ease',
  },
  isPinned: {
    color: theme.palette.facet.primaryBlue,
  },
  loadingItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 1),
    margin: theme.spacing(1),
  },
  fixedHeight: {
    height: 300,
    overflowY: 'scroll',
  },
});

const TemplateList = ({
  className,
  classes,
  menuTitle,
  templateCollection,
  placeholder,
  isLoading,
  listType,
  fixedHeight,
  currentPageNumber,
  onPaginationChange,
  loadingPrevOrNextPage,
  showPagination,
  hasNextPage,
  hasPrevPage,
  ...rest
}) => {
  const renderSavedTemplateList = () => {
    let templateResources = templateCollection?.resources().list();

    // Filter out Facet templates for the insert lists
    if (!listType.includes('insert')) {
      templateResources = templateCollection
        ?.resources()
        .list()
        .filter(resource => resource.meta().field('content').attr('readonly') !== true);
    }

    return templateResources?.length >= 1 ? templateResources.map(templateRes => {
      return (
        <TemplateListItem
          templateRes={templateRes}
          listType={listType}
          {...rest}
        />
      );
    }) : (
      <Typography
        className={classNames(classes.templateListNoItems, {
          [classes.fixedHeight]: fixedHeight,
        })}
        variant={'h6'}
        color={'primaryGray2'}
        weight={'book'}
        test={'no-message-templates-placeholder'}
      >
        {placeholder}
      </Typography>
    );
  };

  return (
    <Fade in>
      <div
        className={classNames(className, classes.root)}
      >
        <FDStack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          <Typography
            className={classes.templateListTitle}
            variant={'h6'}
            color={'primary'}
          >
            {menuTitle}
          </Typography>
          {showPagination ? (
            <FDPagination
              alignSelf={'center'}
              page={currentPageNumber}
              hasNextPage={hasNextPage}
              onChange={(change, newPageNumber) => onPaginationChange(newPageNumber)}
              loading={loadingPrevOrNextPage}
            />
          ) : null}
        </FDStack>
        <div
          className={classNames(classes.templateList, {
            [classes.fixedHeight]: fixedHeight,
          })}
        >
          {isLoading && (
            <div
              test={'template-list-loader'}
              className={classNames({
                [classes.fixedHeight]: fixedHeight,
              })}
            >
              <div className={classes.loadingItem}>
                <Skeleton variant="rect" width={'75%'} height={15} />
                <Skeleton variant="rect" width={15} height={15} />
              </div>
              <div className={classes.loadingItem}>
                <Skeleton variant="rect" width={'75%'} height={15} />
                <Skeleton variant="rect" width={15} height={15} />
              </div>
              <div className={classes.loadingItem}>
                <Skeleton variant="rect" width={'75%'} height={15} />
                <Skeleton variant="rect" width={15} height={15} />
              </div>
              <div className={classes.loadingItem}>
                <Skeleton variant="rect" width={'75%'} height={15} />
                <Skeleton variant="rect" width={15} height={15} />
              </div>
              <div className={classes.loadingItem}>
                <Skeleton variant="rect" width={'75%'} height={15} />
                <Skeleton variant="rect" width={15} height={15} />
              </div>
            </div>
          )}
          {!isLoading
            && (
              renderSavedTemplateList()
            )
          }
        </div>
      </div>
    </Fade>
  );
};

TemplateList.defaultProps = {
  className: '',
  togglePinTemplate: null,
  isLoading: false,
  fixedHeight: false,
  currentPageNumber: 1,
  loadingPrevOrNextPage: false,
  onPaginationChange: noop,
  showPagination: false,
  hasNextPage: false,
  hasPrevPage: false,
};

TemplateList.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  menuTitle: PropTypes.string.isRequired,
  templateCollection: PropTypes.object.isRequired,
  listItemOnClick: PropTypes.func.isRequired,
  togglePinTemplate: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  listType: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  fixedHeight: PropTypes.bool,
  currentPageNumber: PropTypes.number,
  onPaginationChange: PropTypes.func,
  loadingPrevOrNextPage: PropTypes.bool,
  showPagination: PropTypes.bool,
  hasPrevPage: PropTypes.bool,
  hasNextPage: PropTypes.bool,
};

export default withStyles(styles)(TemplateList);
